/**
 * InlineEditField Component
 *
 * Feature 006: Admin Order Editing Without Page Reload
 *
 * Reusable inline edit field with:
 * - Controlled input with onBlur save
 * - Visual feedback states (editing, saving, success, error)
 * - Automatic focus management
 * - Accessibility attributes (WCAG 2.1 AA compliant)
 */

import React from 'react';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import type { InlineEditStatus } from '../../types';

interface InlineEditFieldProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  validate?: (value: string) => string | null;
  inputType?: 'text' | 'number';
  placeholder?: string;
  ariaLabel: string;
  className?: string;
}

export const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  onSave,
  validate,
  inputType = 'text',
  placeholder,
  ariaLabel,
  className = ''
}) => {
  const {
    state,
    value: editingValue,
    handleChange,
    handleBlur,
    handleFocus
  } = useInlineEdit({
    initialValue: value,
    onSave,
    validateFn: validate
  });

  // Get status-specific CSS classes
  const getStatusClass = (status: InlineEditStatus): string => {
    switch (status) {
      case 'editing':
        return 'inline-edit-editing';
      case 'saving':
        return 'inline-edit-saving';
      case 'success':
        return 'inline-edit-success';
      case 'error':
        return 'inline-edit-error';
      default:
        return '';
    }
  };

  // Get status icon
  const getStatusIcon = (status: InlineEditStatus): React.ReactNode => {
    switch (status) {
      case 'saving':
        return <span className="inline-edit-spinner" aria-hidden="true">⏳</span>;
      case 'success':
        return <span className="inline-edit-checkmark" aria-hidden="true">✓</span>;
      case 'error':
        return <span className="inline-edit-error-icon" aria-hidden="true">⚠️</span>;
      default:
        return null;
    }
  };

  return (
    <div className={`inline-edit-container ${className}`}>
      <div className={`inline-edit-field-wrapper ${getStatusClass(state.status)}`}>
        <input
          type={inputType}
          value={editingValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            // Tab triggers save
            if (e.key === 'Tab') {
              e.preventDefault();
              handleBlur();
            }
            // Escape cancels and resets
            if (e.key === 'Escape') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder={placeholder}
          disabled={state.status === 'saving'}
          className={`inline-edit-input ${getStatusClass(state.status)}`}
          aria-label={ariaLabel}
          aria-busy={state.status === 'saving'}
          aria-live="polite"
          aria-describedby={state.error ? 'inline-edit-error' : undefined}
        />

        {/* Status indicator */}
        <span className="inline-edit-status-indicator" aria-live="polite">
          {getStatusIcon(state.status)}
        </span>
      </div>

      {/* Error message */}
      {state.error && (
        <div
          id="inline-edit-error"
          className="inline-edit-error-message"
          role="alert"
          aria-live="assertive"
        >
          {state.error}
        </div>
      )}
    </div>
  );
};
